import React, { useEffect, useState } from 'react';
import { matchesApi, sessionsApi, clientsApi, matchTxApi, sessionTxApi, positionsApi } from '../../lib/api';
import { useSocketStore } from '../../store/socketStore';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { RefreshCw, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export function CompactPage() {
  const { subscribe } = useSocketStore();
  const [matches, setMatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [matchTxns, setMatchTxns] = useState([]);
  const [sessionTxns, setSessionTxns] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [showDeleted, setShowDeleted] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, txnId: null, type: null, reason: '' });

  const [matchForm, setMatchForm] = useState({
    client_id: '', rate: '', amount: '', mode: 'BACK', team: ''
  });
  const [sessionForm, setSessionForm] = useState({
    session_id: '', client_id: '', rate: '', runs: '', amount: '', mode: 'YES'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [matchesData, sessionsData, clientsData] = await Promise.all([
        matchesApi.list('OPEN'),
        sessionsApi.list(null, 'OPEN'),
        clientsApi.list()
      ]);
      setMatches(matchesData || []);
      setSessions(sessionsData || []);
      setClients(clientsData || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (matchId) => {
    if (!matchId) return;
    try {
      const [mTxns, sTxns, pos] = await Promise.all([
        matchTxApi.list(matchId, showDeleted),
        sessionTxApi.list(null, matchId, showDeleted),
        positionsApi.list(matchId)
      ]);
      setMatchTxns(mTxns || []);
      setSessionTxns(sTxns || []);
      setPositions(pos || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setMatchTxns([]);
      setSessionTxns([]);
      setPositions([]);
    }
  };

  useEffect(() => {
    fetchData();
    const unsubs = [
      subscribe('TRANSACTION_CREATED', () => fetchTransactions(selectedMatch)),
      subscribe('MATCH_DECLARED', fetchData),
      subscribe('SESSION_DECLARED', fetchData)
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe, selectedMatch]);

  useEffect(() => {
    if (selectedMatch) {
      fetchTransactions(selectedMatch);
    } else {
      setMatchTxns([]);
      setSessionTxns([]);
      setPositions([]);
    }
  }, [selectedMatch, showDeleted]);

  const handleDeleteMatchTxn = (txnId, isPatti) => {
    if (isPatti) {
      toast.error('This is a Patti auto-generated transaction and cannot be deleted directly. Delete the source transaction instead.');
      return;
    }
    setDeleteDialog({ open: true, txnId, type: 'match', reason: '' });
  };

  const handleDeleteSessionTxn = (txnId, isPatti) => {
    if (isPatti) {
      toast.error('This is a Patti auto-generated transaction and cannot be deleted directly. Delete the source transaction instead.');
      return;
    }
    setDeleteDialog({ open: true, txnId, type: 'session', reason: '' });
  };

  const confirmDelete = async () => {
    try {
      if (deleteDialog.type === 'match') {
        await matchTxApi.delete(deleteDialog.txnId, deleteDialog.reason);
      } else {
        await sessionTxApi.delete(deleteDialog.txnId, deleteDialog.reason);
      }
      toast.success('Transaction deleted');
      setDeleteDialog({ open: false, txnId: null, type: null, reason: '' });
      fetchTransactions(selectedMatch);
    } catch (error) {
      toast.error(error.message || 'Failed to delete transaction');
    }
  };

  const handleMatchTxSubmit = async () => {
    try {
      const data = {
        match_id: selectedMatch,
        client_id: matchForm.client_id,
        rate: parseFloat(matchForm.rate),
        amount: parseFloat(matchForm.amount),
        mode: matchForm.mode,
        team: matchForm.team
      };

      if (!data.client_id || !data.rate || !data.amount || !data.team) {
        toast.error('All fields are required');
        return;
      }

      await matchTxApi.create(data);
      toast.success('Match transaction added');
      setMatchForm({ client_id: '', rate: '', amount: '', mode: 'BACK', team: '' });
      fetchTransactions(selectedMatch);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSessionTxSubmit = async () => {
    try {
      const data = {
        session_id: sessionForm.session_id,
        client_id: sessionForm.client_id,
        rate: parseFloat(sessionForm.rate),
        runs: parseInt(sessionForm.runs),
        amount: parseFloat(sessionForm.amount),
        mode: sessionForm.mode
      };

      if (!data.session_id || !data.client_id || !data.rate || !data.runs || !data.amount) {
        toast.error('All fields are required');
        return;
      }

      await sessionTxApi.create(data);
      toast.success('Session transaction added');
      setSessionForm({ session_id: '', client_id: '', rate: '', runs: '', amount: '', mode: 'YES' });
      fetchTransactions(selectedMatch);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const selectedMatchData = matches.find(m => m.match_id === selectedMatch);
  const filteredSessions = sessions.filter(s => s.match_id === selectedMatch);

  const getClientName = (clientId) => {
    const c = clients.find(c => c.client_id === clientId);
    return c?.name?.slice(0, 12) || '?';
  };

  const matchTxColumns = [
    { header: 'Time', render: (row) => {
      try { return row.created_at ? format(new Date(row.created_at), 'HH:mm') : '-'; }
      catch { return '-'; }
    }},
    { header: 'Client', render: (row) => getClientName(row.client_id) },
    { header: 'Mode', render: (row) => <StatusBadge status={row.mode} /> },
    { header: 'Team', accessor: 'team' },
    { header: 'Rate', accessor: 'rate', type: 'number' },
    { header: 'Amt', accessor: 'amount', type: 'number' },
    { header: 'A', accessor: 'team_a_impact', type: 'profit' },
    { header: 'B', accessor: 'team_b_impact', type: 'profit' },
    {
      header: '',
      render: (row) => {
        if (row.is_deleted) {
          return <span className="text-[10px] px-1 py-0.5 rounded bg-red-900/30 text-red-400">DEL</span>;
        }
        if (row.is_patti || row.transaction_origin === 'PATTI_AUTO') return null;
        return (
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-destructive" onClick={() => handleDeleteMatchTxn(row.txn_id, row.is_patti)} data-testid={`compact-del-match-${row.txn_id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        );
      }
    }
  ];

  const sessionTxColumns = [
    { header: 'Time', render: (row) => {
      try { return row.created_at ? format(new Date(row.created_at), 'HH:mm') : '-'; }
      catch { return '-'; }
    }},
    { header: 'Session', render: (row) => {
      const s = sessions.find(s => s.session_id === row.session_id);
      return s?.name?.slice(0, 15) || '?';
    }},
    { header: 'Client', render: (row) => getClientName(row.client_id) },
    { header: 'Mode', render: (row) => <StatusBadge status={row.mode} /> },
    { header: 'Rate', accessor: 'rate', type: 'number' },
    { header: 'Run', accessor: 'runs', type: 'number' },
    { header: 'Amt', accessor: 'amount', type: 'number' },
    {
      header: '',
      render: (row) => {
        if (row.is_deleted) {
          return <span className="text-[10px] px-1 py-0.5 rounded bg-red-900/30 text-red-400">DEL</span>;
        }
        if (row.is_patti || row.transaction_origin === 'PATTI_AUTO') return null;
        return (
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-destructive" onClick={() => handleDeleteSessionTxn(row.txn_id, row.is_patti)} data-testid={`compact-del-session-${row.txn_id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        );
      }
    }
  ];

  // Match book from positions
  const mainBook = (positions || []).reduce((acc, pos) => {
    acc.team_a += pos.team_a_position || 0;
    acc.team_b += pos.team_b_position || 0;
    return acc;
  }, { team_a: 0, team_b: 0 });

  return (
    <div className="space-y-4" data-testid="compact-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">
          Compact Entry <span className="kbd ml-2">F6</span>
        </h1>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs gap-1"
            onClick={() => setShowDeleted(!showDeleted)}
            data-testid="compact-toggle-deleted"
          >
            {showDeleted ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-compact">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Match Selection */}
      <GroupBox label="Active Match">
        <Select value={selectedMatch} onValueChange={setSelectedMatch}>
          <SelectTrigger className="h-8" data-testid="compact-select-match">
            <SelectValue placeholder="Select an active match" />
          </SelectTrigger>
          <SelectContent>
            {matches.map(m => (
              <SelectItem key={m.match_id} value={m.match_id}>
                {m.name} - {m.team_a} vs {m.team_b}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </GroupBox>

      {loading && selectedMatch && (
        <div className="text-center py-8 text-muted-foreground" data-testid="compact-loading">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
          Loading match data...
        </div>
      )}

      {!loading && selectedMatch && !selectedMatchData && (
        <div className="text-center py-8 text-muted-foreground" data-testid="compact-empty">
          Match not found or no longer active.
        </div>
      )}

      {selectedMatch && selectedMatchData && (
        <>
          {/* Main Book Summary */}
          <GroupBox label="Main Book" className="p-2">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">{selectedMatchData?.team_a || 'Team A'}</p>
                <p className={`font-mono text-lg font-bold ${mainBook.team_a >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {mainBook.team_a.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{selectedMatchData?.team_b || 'Team B'}</p>
                <p className={`font-mono text-lg font-bold ${mainBook.team_b >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {mainBook.team_b.toFixed(2)}
                </p>
              </div>
            </div>
          </GroupBox>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Left Panel - Match */}
            <div className="space-y-4">
              <GroupBox label="Match Transaction Entry">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-3">
                    <Label className="text-xs">Client</Label>
                    <Select value={matchForm.client_id} onValueChange={(v) => setMatchForm({...matchForm, client_id: v})}>
                      <SelectTrigger className="h-8" data-testid="compact-match-client">
                        <SelectValue placeholder="Client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Rate</Label>
                    <Input 
                      type="number" 
                      value={matchForm.rate}
                      onChange={(e) => setMatchForm({...matchForm, rate: e.target.value})}
                      className="h-8"
                      data-testid="compact-match-rate"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input 
                      type="number" 
                      value={matchForm.amount}
                      onChange={(e) => setMatchForm({...matchForm, amount: e.target.value})}
                      className="h-8"
                      data-testid="compact-match-amount"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mode</Label>
                    <Select value={matchForm.mode} onValueChange={(v) => setMatchForm({...matchForm, mode: v})}>
                      <SelectTrigger className="h-8" data-testid="compact-match-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BACK">BACK</SelectItem>
                        <SelectItem value="LAY">LAY</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Team</Label>
                    <Select value={matchForm.team} onValueChange={(v) => setMatchForm({...matchForm, team: v})}>
                      <SelectTrigger className="h-8" data-testid="compact-match-team">
                        <SelectValue placeholder="Team" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedMatchData && (
                          <>
                            <SelectItem value={selectedMatchData.team_a}>{selectedMatchData.team_a}</SelectItem>
                            <SelectItem value={selectedMatchData.team_b}>{selectedMatchData.team_b}</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-2">
                  <Button size="sm" onClick={handleMatchTxSubmit} data-testid="compact-match-submit" className="w-full">
                    Add Match Txn
                  </Button>
                </div>
              </GroupBox>

              <GroupBox label="Match Transactions">
                <div className="max-h-64 overflow-auto">
                  <DenseTable 
                    columns={matchTxColumns} 
                    data={matchTxns.slice(0, 30)}
                    emptyMessage="No transactions"
                    rowClassName={(row) => {
                      if (row.is_deleted) return 'opacity-50 bg-red-50 dark:bg-red-950/20 line-through';
                      if (row.is_patti) return 'bg-orange-50 dark:bg-orange-950/20';
                      return '';
                    }}
                  />
                </div>
              </GroupBox>
            </div>

            {/* Right Panel - Session */}
            <div className="space-y-4">
              <GroupBox label="Session Transaction Entry">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-3">
                    <Label className="text-xs">Session</Label>
                    <Select value={sessionForm.session_id} onValueChange={(v) => setSessionForm({...sessionForm, session_id: v})}>
                      <SelectTrigger className="h-8" data-testid="compact-session-select">
                        <SelectValue placeholder="Session" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredSessions.map(s => (
                          <SelectItem key={s.session_id} value={s.session_id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Client</Label>
                    <Select value={sessionForm.client_id} onValueChange={(v) => setSessionForm({...sessionForm, client_id: v})}>
                      <SelectTrigger className="h-8" data-testid="compact-session-client">
                        <SelectValue placeholder="Client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Rate</Label>
                    <Input 
                      type="number" 
                      value={sessionForm.rate}
                      onChange={(e) => setSessionForm({...sessionForm, rate: e.target.value})}
                      className="h-8"
                      data-testid="compact-session-rate"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Run</Label>
                    <Input 
                      type="number" 
                      value={sessionForm.runs}
                      onChange={(e) => setSessionForm({...sessionForm, runs: e.target.value})}
                      className="h-8"
                      data-testid="compact-session-run"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input 
                      type="number" 
                      value={sessionForm.amount}
                      onChange={(e) => setSessionForm({...sessionForm, amount: e.target.value})}
                      className="h-8"
                      data-testid="compact-session-amount"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Mode</Label>
                    <Select value={sessionForm.mode} onValueChange={(v) => setSessionForm({...sessionForm, mode: v})}>
                      <SelectTrigger className="h-8" data-testid="compact-session-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="YES">YES</SelectItem>
                        <SelectItem value="NO">NO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-2">
                  <Button size="sm" onClick={handleSessionTxSubmit} data-testid="compact-session-submit" className="w-full">
                    Add Session Txn
                  </Button>
                </div>
              </GroupBox>

              <GroupBox label="Session Transactions">
                <div className="max-h-64 overflow-auto">
                  <DenseTable 
                    columns={sessionTxColumns} 
                    data={sessionTxns.slice(0, 30)}
                    emptyMessage="No transactions"
                    rowClassName={(row) => {
                      if (row.is_deleted) return 'opacity-50 bg-red-50 dark:bg-red-950/20 line-through';
                      if (row.is_patti) return 'bg-orange-50 dark:bg-orange-950/20';
                      return '';
                    }}
                  />
                </div>
              </GroupBox>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, txnId: null, type: null, reason: '' })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Transaction
            </DialogTitle>
            <DialogDescription>
              This transaction will be soft-deleted. It remains visible but is excluded from all calculations. Any linked Patti transactions will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea
                placeholder="Enter reason..."
                value={deleteDialog.reason}
                onChange={(e) => setDeleteDialog({ ...deleteDialog, reason: e.target.value })}
                className="mt-1"
                rows={2}
                data-testid="compact-delete-reason"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, txnId: null, type: null, reason: '' })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="compact-confirm-delete">
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
